// Public landing for /accept-invite/:token.  Loads invitation info,
// shows the role + expiry, lets the invitee finish their own profile
// (name + login + password [+ optional PIN]).  On submit creates the
// operator + auto-logs in (server issues tokens), routes home.

import { useMutation, useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Navigate, useNavigate, useParams } from "react-router-dom";

import { ErrorBanner } from "@/components/ErrorBanner";
import { ErrorState, LoadingState } from "@/components/States";
import { request } from "@/lib/api";

const ACCESS_KEY = "mes:auth:access";
const REFRESH_KEY = "mes:auth:refresh";

interface InvitationPublic {
  role: string;
  email: string | null;
  expires_at: string;
}

interface TokenPair {
  access_token: string;
  refresh_token: string;
  token_type: "Bearer";
}

export default function AcceptInvite() {
  const { t } = useTranslation();
  const { token } = useParams<{ token: string }>();
  const nav = useNavigate();

  const [name, setName] = useState("");
  // Fallback login — used only when the invitation has no email
  // attached (legacy link-only invites).  For email-bearing invites
  // the backend uses invite.email as the login automatically.
  const [login, setLogin] = useState("");
  const [password, setPassword] = useState("");
  const [pin, setPin] = useState("");

  const info = useQuery({
    queryKey: ["invitation", token],
    queryFn: () => request<InvitationPublic>(`/auth/invitation/${token}`),
    enabled: !!token,
    retry: false,
  });

  const accept = useMutation({
    mutationFn: () =>
      request<TokenPair>(`/auth/invitation/${token}/accept`, {
        method: "POST",
        body: JSON.stringify({
          name: name.trim(),
          ...(info.data?.email ? {} : { login: login.trim() }),
          password,
          ...(pin.trim() ? { pin: pin.trim() } : {}),
        }),
      }),
    onSuccess: (tok) => {
      localStorage.setItem(ACCESS_KEY, tok.access_token);
      localStorage.setItem(REFRESH_KEY, tok.refresh_token);
      window.dispatchEvent(new Event("mes:auth-change"));
      nav("/", { replace: true });
    },
  });

  if (!token) return <Navigate to="/login" replace />;

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-page">
      <div className="w-full max-w-md card p-8 space-y-5">
        <header>
          <h1 className="text-2xl font-serif">{t("acceptInvite.welcome")}</h1>
          <p className="text-text-muted text-sm mt-1">
            {t("acceptInvite.subtitle")}
          </p>
        </header>

        {info.isLoading && <LoadingState />}
        {info.isError && (
          <div className="space-y-3">
            <ErrorState error={info.error} />
            <p className="text-text-muted text-sm">
              {t("acceptInvite.linkInvalid")}
            </p>
          </div>
        )}

        {info.data && (
          <>
            <div className="card p-3 bg-surface-alt text-xs space-y-1">
              <p>
                {t("acceptInvite.role")} <span className="pill pill-accent">{info.data.role}</span>
              </p>
              {info.data.email && (
                <p>
                  {t("acceptInvite.recipient")} <span className="font-mono">{info.data.email}</span>
                </p>
              )}
              <p className="text-text-muted">
                {t("acceptInvite.expires")} <span className="font-mono">
                  {new Date(info.data.expires_at).toLocaleString("ru")}
                </span>
              </p>
            </div>

            <form
              className="space-y-3"
              onSubmit={(e) => {
                e.preventDefault();
                accept.mutate();
              }}
            >
              <Field label={t("acceptInvite.fullName")}>
                <input
                  className="w-full px-3 py-2 rounded-md bg-surface border border-border"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                />
              </Field>
              {info.data.email ? (
                <Field label={t("acceptInvite.loginEmail")}>
                  <input
                    readOnly
                    className="w-full px-3 py-2 rounded-md bg-surface-alt border border-border font-mono text-text-muted"
                    value={info.data.email}
                  />
                </Field>
              ) : (
                <Field label={t("acceptInvite.login")}>
                  <input
                    className="w-full px-3 py-2 rounded-md bg-surface border border-border font-mono"
                    value={login}
                    onChange={(e) => setLogin(e.target.value)}
                    required
                    autoComplete="username"
                  />
                </Field>
              )}
              <Field label={t("acceptInvite.password")}>
                <input
                  type="password"
                  className="w-full px-3 py-2 rounded-md bg-surface border border-border"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  minLength={8}
                  required
                  autoComplete="new-password"
                />
              </Field>
              <Field label={t("acceptInvite.pin")}>
                <input
                  className="w-full px-3 py-2 rounded-md bg-surface border border-border font-mono"
                  value={pin}
                  onChange={(e) => setPin(e.target.value)}
                  inputMode="numeric"
                />
              </Field>

              {accept.isError && (
                <ErrorBanner onDismiss={() => accept.reset()}>
                  {(accept.error as Error).message}
                </ErrorBanner>
              )}

              <button
                type="submit"
                className="btn btn-primary w-full"
                disabled={accept.isPending}
              >
                {accept.isPending ? t("acceptInvite.registering") : t("acceptInvite.submit")}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="text-xs font-mono uppercase tracking-wider text-text-muted">
        {label}
      </span>
      <div className="mt-1">{children}</div>
    </label>
  );
}
